
import React, { useState, useEffect } from 'react';
import { Cookie, Settings } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { useCookieConsent, CookieConsent } from '@/contexts/CookieConsentContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

const CookiePreferences: React.FC = () => {
  const { consent, updateConsent, acceptAll, rejectAll, isEuUser } = useCookieConsent();
  const [preferences, setPreferences] = useState<CookieConsent>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (consent) {
      setPreferences(consent);
    }
  }, [consent]);

  const handleSavePreferences = () => {
    updateConsent(preferences);
    toast.success('Cookie preferences updated successfully');
  };

  const handlePreferenceChange = (category: keyof CookieConsent, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [category]: category === 'essential' ? true : value, // Essential cookies cannot be disabled
    }));
  };

  if (!isEuUser) {
    return (
      <PageLayout title="Cookie Preferences">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Cookie className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Cookie Preferences</h3>
                <p className="text-muted-foreground">
                  Cookie preferences are only available for EU users. We've detected you're not in the EU, so all cookies are enabled by default.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Cookie Preferences" 
      description="Manage your cookie preferences and privacy settings"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-blue-600" />
              <CardTitle>Cookie Settings</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1 pr-4">
                  <Label htmlFor="essential" className="font-medium">Essential Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Required for the website to function properly, including authentication and basic navigation. These cannot be disabled.
                  </p>
                </div>
                <Switch
                  id="essential"
                  checked={true}
                  disabled={true}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1 pr-4">
                  <Label htmlFor="analytics" className="font-medium">Analytics Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how you use our website through PostHog analytics. This data helps us improve your experience and fix issues.
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => handlePreferenceChange('analytics', checked)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1 pr-4">
                  <Label htmlFor="marketing" className="font-medium">Marketing Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Used by Meta Pixel to show you relevant ads on Facebook and Instagram and measure the effectiveness of our campaigns.
                  </p>
                </div>
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => handlePreferenceChange('marketing', checked)}
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button onClick={handleSavePreferences} className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Save Preferences
              </Button>
              <Button variant="outline" onClick={acceptAll}>
                Accept All
              </Button>
              <Button variant="outline" onClick={rejectAll}>
                Reject All
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>About Cookies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              Cookies are small text files that are stored on your computer or device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
            </p>
            <p>
              <strong>Essential cookies</strong> are necessary for the website to function and cannot be switched off. 
              They are usually only set in response to actions made by you such as logging in or setting privacy preferences.
            </p>
            <p>
              <strong>Analytics cookies</strong> help us understand how visitors interact with our website by collecting and reporting information anonymously.
            </p>
            <p>
              <strong>Marketing cookies</strong> are used to track visitors across websites to display ads that are relevant and engaging.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default CookiePreferences;
